import gql from 'graphql-tag';

import { type CustomerPersona, mapPersonaForBackendInput } from 'src/data/persona';
import { executeGqlMutation } from 'src/graphql/client';
import { FRAGMENT_JOB_ID, waitForJobWithEntityResult } from 'src/graphql/job';
import type { ID } from 'src/lib/collection';
import { stringifyIfPresent } from 'src/lib/common';
import { getGqlFragmentName, getSingleProp } from 'src/lib/graphql';
import type {
  BackendMutationSuggestVariationsForDocumentEditorArgs,
  BackendSuggestionRequest,
} from 'src/types/backend';

import { FRAGMENT_SUGGEST_REQUEST } from './schema';

type Variables = Omit<
  BackendMutationSuggestVariationsForDocumentEditorArgs,
  'async' | 'customerPersonaId' | 'customerPersona'
> & {
  customerPersona?: CustomerPersona;
};
type Result = BackendSuggestionRequest;

interface Options {
  async?: boolean;
  abortSignal?: AbortSignal;
  onJobProgress?: (data: unknown) => void;
}

/**
 * "suggest" generation for short-form
 */
export const suggestDocumentVariations = async (
  {
    subGenerationToolsProps,
    freestyleToolAllInstructionsData,
    customerPersona,
    structureInstructionsData,
    ...rest
  }: Variables,
  options: Options | null = null
) => {
  const resultFragment = FRAGMENT_SUGGEST_REQUEST;
  const fragment = options?.async ? FRAGMENT_JOB_ID : resultFragment;
  const mutation = gql`
    mutation SuggestDocumentVariations(
      $async: Boolean
      $projectId: ID!
      $generationTool: GenerationToolEnum!
      $subGenerationToolsProps: JSON
      $brief: BriefInput!
      $inputText: String
      $scoringMethod: ScoringMethodInput
      $suggestionMode: String!
      $language: String!
      $languageTargetFormality: LanguageTargetFormalityEnum
      $numberOfVariations: Int!
      $tovInput: TovInput
      $debugAcPrompt: String
      $freestyleToolId: ID
      $freestyleToolAllInstructions: String
      $freestyleToolAllInstructionsData: JSON
      $customerPersonaId: ID
      $customerPersona: CustomerPersonaInput
      $annotations: [SuggestionAnnotationInput!]
      $sourceType: String
      $url: String
      $manualInstructions: String
      $avoidWords: [String!]
      $onlyImproveScore: Boolean
      $originalVariationId: ID
      $extractedBrief: String
      $urlExtractions: [UrlExtractionInput!]
      $callToAction: String
      $generationCustomModelId: ID
      $imageAspectRatio: String
      $imageModel: String
      $imageStyleId: ID
      $imageOverlayProperties: ImageOverlayTextInput
      $availableTools: JSON
      $structureInstructionsData: JSON
      $mediaGenerationProperties: MediaGenerationInput
      $subTemplateProperties: SubTemplateInput
    ) {
      suggestVariationsForDocumentEditor(
        async: $async
        projectId: $projectId
        generationTool: $generationTool
        subGenerationToolsProps: $subGenerationToolsProps
        brief: $brief
        inputText: $inputText
        scoringMethod: $scoringMethod
        suggestionMode: $suggestionMode
        language: $language
        languageTargetFormality: $languageTargetFormality
        numberOfVariations: $numberOfVariations
        tovInput: $tovInput
        debugAcPrompt: $debugAcPrompt
        freestyleToolId: $freestyleToolId
        freestyleToolAllInstructions: $freestyleToolAllInstructions
        freestyleToolAllInstructionsData: $freestyleToolAllInstructionsData
        customerPersonaId: $customerPersonaId
        customerPersona: $customerPersona
        annotations: $annotations
        sourceType: $sourceType
        url: $url
        manualInstructions: $manualInstructions
        avoidWords: $avoidWords
        onlyImproveScore: $onlyImproveScore
        originalVariationId: $originalVariationId
        extractedBrief: $extractedBrief
        urlExtractions: $urlExtractions
        callToAction: $callToAction
        generationCustomModelId: $generationCustomModelId
        imageAspectRatio: $imageAspectRatio
        imageModel: $imageModel
        imageStyleId: $imageStyleId
        imageOverlayProperties: $imageOverlayProperties
        availableTools: $availableTools
        structureInstructionsData: $structureInstructionsData
        mediaGenerationProperties: $mediaGenerationProperties
        subTemplateProperties: $subTemplateProperties
      ) {
        ...${getGqlFragmentName(fragment)}
      }
    }
    ${fragment}
  `;

  const variables = {
    ...rest,
    async: options?.async ?? false,
    subGenerationToolsProps: stringifyIfPresent(subGenerationToolsProps),
    freestyleToolAllInstructionsData: stringifyIfPresent(freestyleToolAllInstructionsData),
    customerPersonaId: customerPersona?.id,
    customerPersona: customerPersona ? mapPersonaForBackendInput(customerPersona) : null,
    structureInstructionsData: stringifyIfPresent(structureInstructionsData),
  };

  const response = await executeGqlMutation({
    mutation,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  const result = getSingleProp<Result | Result[]>(response.data);
  if (!options?.async) {
    // support old BE, which returns a single object instead of an array
    if (!result) {
      return [];
    }
    return Array.isArray(result) ? result : [result];
  }

  const job = Array.isArray(result) ? result[0] : result;
  return waitForJobWithEntityResult<Result>((job as { id: ID }).id, resultFragment, {
    abortSignal: options?.abortSignal,
    onProgress: options?.onJobProgress,
    list: true,
  });
};
